import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { createPortal } from "react-dom";
import CrossIcon from "@/Components/Common/Icons/CrossIcon";

const ProductsListingPopUp = ({ filteredList, onClose, loading = false }) => {
  const [mounted, setMounted] = useState(false);
  const portalContainerRef = useRef(null);

  useEffect(() => {
    // Create portal container
    let container = document.getElementById('products-listing-portal');

    if (!container) {
      container = document.createElement('div');
      container.id = 'products-listing-portal';
      document.body.appendChild(container);
    }

    portalContainerRef.current = container;
    setMounted(true);

    // Prevent body scroll when modal is open
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
      try {
        const containerToRemove = portalContainerRef.current;
        if (containerToRemove && document.body.contains(containerToRemove)) {
          document.body.removeChild(containerToRemove);
        }
        portalContainerRef.current = null;
      } catch (error) {
        console.warn('Portal container cleanup:', error);
      }
    };
  }, []);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!mounted || !portalContainerRef.current) return null;

  const modalContent = (
    <div
      className="fixed inset-0 popup-bg flex justify-center z-[9999999] items-end sm:items-center"
      onClick={handleBackdropClick}
    >
      <div className="w-full sm:w-[600px] lg:w-[800px] bg-white z-50 rounded-none sm:rounded-xl shadow-2xl border border-neutral-200 overflow-hidden h-full sm:h-auto sm:max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-4 sm:px-6 pt-4 sm:pt-5 pb-3 sm:pb-4 border-b border-neutral-200 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 font-poppins">
                Products
              </h2>
              <p className="text-neutral-500 text-[11px] sm:text-xs font-medium font-poppins mt-0.5">
                Showing all {filteredList ? filteredList?.length : 0} results
              </p>
            </div>
            <div
              onClick={onClose}
              className="cursor-pointer hover:bg-gray-100 rounded-full p-1.5 transition-colors"
            >
              <CrossIcon />
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="px-4 sm:px-6 py-4 flex-1 overflow-y-auto">
          <div className="flex flex-col gap-2 sm:gap-3 w-full">
            {loading ? (
              <div className="w-full p-8 flex flex-col items-center justify-center text-center">
                <div className="loading-circle mb-3"></div>
                <p className="text-sm text-gray-500 font-poppins">Loading products...</p>
              </div>
            ) : filteredList && filteredList?.length > 0 ? (
              filteredList?.map((ele, key) => (
                <Link className="w-full" href={`/products/${ele.type}/${ele.productAlias}`} key={key}>
                  <div className="w-full p-2.5 sm:p-4 cursor-pointer flex bg-white rounded-lg border border-zinc-200 hover:border-blue-300 hover:shadow-sm transition-all">
                    <div className="flex flex-row gap-3 sm:gap-4 w-full">
                      <div className="shrink-0">
                        <Image
                          src={"/Images/placeholder_medicine.svg"}
                          width={91}
                          height={91}
                          alt="medicine"
                          className="w-[50px] h-[50px] sm:w-[70px] sm:h-[70px] object-contain"
                        />
                      </div>
                      <div className="flex flex-col font-poppins min-w-0 flex-1 justify-center">
                        <h1 className="font-poppins text-[#1F1F1F] text-[14px] sm:text-[16px] font-semibold truncate">
                          {ele?.openfda?.brand_name}
                        </h1>
                        <div className="flex flex-col sm:flex-row gap-0 sm:gap-2 mt-1">
                          <p className="font-normal font-poppins text-[11px] sm:text-[13px] text-[#4B4B4B]">
                            <span className="text-[#8E8E8E]">Generic:</span>{" "}
                            <span className="truncate">{ele?.openfda.generic_name}</span>
                          </p>
                        </div>
                        <p className="font-normal font-poppins text-[11px] sm:text-[13px] text-[#4B4B4B] capitalize truncate">
                          <span className="text-[#8E8E8E]">Manufacturer:</span>{" "}
                          {ele?.openfda.manufacturer_name}
                        </p>
                      </div>
                    </div>
                  </div>
                </Link>
              ))
            ) : (
              <div className="w-full p-8 flex flex-col items-center justify-center text-center">
                <p className="text-sm text-gray-500 font-poppins">No products found</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 flex-shrink-0 bg-white">
          <button
            type="button"
            onClick={onClose}
            className="w-full border-2 border-gray-300 text-gray-700 text-sm sm:text-base font-semibold font-poppins rounded-lg py-2.5 sm:py-3 hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {portalContainerRef.current && createPortal(modalContent, portalContainerRef.current)}
    </>
  );
};

export default ProductsListingPopUp;
